export type MarketingThemeId =
  | 'signal-teal'
  | 'trust-navy'
  | 'midnight-cyan'
  | 'slate-amber'
  | 'studio-lilac'
  | 'mono-modern';

export interface MarketingTheme {
  id: MarketingThemeId;
  name: string;
  tagline: string;
  why: string;
  swatches: [string, string, string];
}

/**
 * Color schemes tuned for voice / CX / regulated verticals (banking, real estate, field service).
 * Avoids generic “AI purple” as the default while offering optional softer/demo-site palettes.
 */
export const MARKETING_THEMES: MarketingTheme[] = [
  {
    id: 'signal-teal',
    name: 'Signal Teal',
    tagline: 'Listening & clarity',
    why: 'Teal reads as “signal / mic live.” Calm enough for banking, distinctive for a voice product. Warm CTA keeps try-demo energy.',
    swatches: ['#0f172a', '#0d9488', '#ea580c'],
  },
  {
    id: 'trust-navy',
    name: 'Trust Navy',
    tagline: 'Enterprise & compliance',
    why: 'Deep navy + sky is the fintech / contact-center trust palette. Coral CTA still pulls attention to demos without feeling playful.',
    swatches: ['#0b1f3a', '#2563eb', '#f43f5e'],
  },
  {
    id: 'midnight-cyan',
    name: 'Midnight Cyan',
    tagline: 'Modern voice-tech',
    why: 'Darker canvas + electric cyan feels like a live audio product. Strong for demos and “agent listening” stages; still professional.',
    swatches: ['#060b14', '#22d3ee', '#fbbf24'],
  },
  {
    id: 'slate-amber',
    name: 'Slate Amber',
    tagline: 'Ops & action',
    why: 'Cool slate for tooling seriousness; amber for “take action / call now.” Fits HVAC and field workflows as well as ops consoles.',
    swatches: ['#111827', '#64748b', '#d97706'],
  },
  {
    id: 'studio-lilac',
    name: 'Studio Lilac',
    tagline: 'Warm demo-site feel',
    why: 'Inspired by HuskyVoice’s live-demo look: warm cream canvas, soft violet accent, apricot CTA. Friendly and demo-forward without a cold tech vibe.',
    swatches: ['#171321', '#7c5cbf', '#e0a15a'],
  },
  {
    id: 'mono-modern',
    name: 'Mono Modern',
    tagline: 'Minimal & precise',
    why: 'Near-white canvas, near-black type, almost no chroma. Very modern SaaS restraint — lets the product and demos carry the personality.',
    swatches: ['#09090b', '#71717a', '#18181b'],
  },
];

export const DEFAULT_MARKETING_THEME: MarketingThemeId = 'signal-teal';

export const MARKETING_THEME_STORAGE_KEY = 'vhearu.marketingTheme';

export function isMarketingThemeId(value: string | null | undefined): value is MarketingThemeId {
  return MARKETING_THEMES.some((t) => t.id === value);
}
